import { useMemo, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table, TableProps } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { toTitleCase } from 'Helpers/StringUtils';
import { getCreateIssueUrl, getIssueDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { IssueHistoryModificationsResponse, IssuePriority, IssueResponse, IssueStatus, ManageIssueOrigin, priorityComparator, statusComparator } from 'Models/Issues';
import { OperationalControl } from 'Models/OperationalControls';
import { OrganizationArchiveBase, OrganizationResponseBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ColorTheme } from 'Models/Types/GlobalType';
import { IssueHistoryModal } from 'Pages/IssuesExceptions/Issues/Components/IssueHistoryModal/IssueHistoryModal';

import styles from './IssuesListing.module.css';

/**
 * @param colorTheme    - The type of background that the component will be displayed against. If not supplied, `'light'` will be used.
 * @param hideTitle     - If `true`, the title will not be displayed. This is useful since sometimes this component is used in a tab of the same name, making the title redundant. In other cases, Issues and Exceptions are shown together, so having a title for each is necessary to differentiate them.
 */
export interface IssuesListingBaseProps {
    createButtonVariant: 'primaryButton' | 'secondaryButton';
    createIssueOrigin: ManageIssueOrigin;

    colorTheme?: ColorTheme;
    hideTitle?: boolean;
    hideCreateButton?: boolean;
    useHorizontalRuleBelowTitle?: boolean; // This could be cleaner, but it's the fastest way to remain backwards-compatible for now (since our theming is inconsistent at worst and difficult to get right in child components at best, IMO).
}

export interface IssuesListingControlsProps extends IssuesListingBaseProps {
    type: ProductModule.CONTROLS;
    issues: IssueResponse[];
    preselectedControlIdForCreate?: string;
    displayMappedControlsModal: (mappedControls: OperationalControl[]) => void;
    organizations?: never; // Typed like this so that `organizations` can be used unconditionally as a `useMemo` dependency.
}

export interface IssuesListingDueDiligenceProps extends IssuesListingBaseProps {
    type: DueDiligenceModule;
    issues: IssueResponse[] | IssueHistoryModificationsResponse[];
    organizations: (OrganizationResponseBase | OrganizationArchiveBase)[];
    documentApi: DocumentApi;
    preselectedOrganizationIdForCreate?: string;
    preselectedOrganizationIdForFilter?: string;
    disableOrganizationFilter?: boolean;
    hideOrganizationInfo?: boolean; // TODO: Implement this more cleanly. Possibly remove the above prop.
    /** When `true`, `issues` are `IssueHistoryModificationsResponse`, whose `tags` are already-resolved `DisplayableTag`s rather than tag IDs. This drives whether the Tags column looks up current tag metadata or renders the historical tags directly. */
    historical?: boolean;
}

export type IssuesListingProps = IssuesListingControlsProps | IssuesListingDueDiligenceProps;

/**
 * Renders a table of issues, which are either Operational Controls issues or due diligence (TPRM or UCRM) issues.
 * The `type` prop determines which type of issues are displayed. This affects some minor things, such as whether a "Controls" column or an organization ("Third Party" or "Business") column is rendered.
 *
 * In addition to the table, this component renders:
 * * A header section with the text "issues" and a "Create Issue" button.
 * * Filters for owner, priority, status, and organization (if TPRM or UCRM issues are being listed).
 */
export const IssuesListing = (props: IssuesListingProps) => {
    const cachedData = useCachedData();
    const users = cachedData.users;

    // Historical issues may have since been deleted, so instead of linking to a (possibly missing) details page, clicking one opens a read-only snapshot modal. Only ever set in TPRM historical mode.
    const [selectedHistoricalIssue, setSelectedHistoricalIssue] = useState<IssueHistoryModificationsResponse>();

    const organizationIdToNameMap: Map<string, string> | undefined = useMemo(() => {
        return props.type !== ProductModule.CONTROLS ? new Map(props.organizations.map((organization) => [organization.id, organization.name])) : undefined;
    }, [props.type, props.organizations]);

    const getOrganizationName = organizationIdToNameMap ? (organizationId: string) => organizationIdToNameMap.get(organizationId)! : undefined;

    const columns: ColumnConfig<IssueResponse | IssueHistoryModificationsResponse>[] = [
        {
            type: ColumnType.STRING,
            name: 'Title',
            getString: (issue) => issue.title,
            getOnClickTextAction: (issue) => (props.type !== ProductModule.CONTROLS && props.historical ? { execute: (row) => setSelectedHistoricalIssue(row as IssueHistoryModificationsResponse) } : { goToLocation: getIssueDetailsUrl(issue.id, issue.type) }),
        },
        {
            type: ColumnType.DATE,
            name: 'Due',
            getDateString: (issue) => issue.due_date,
        },
        {
            type: ColumnType.STRING,
            name: 'Source',
            getString: (issue) => issue.source,
        },
        {
            type: ColumnType.USER,
            name: 'Owner',
            getUserId: (issue) => issue.owner_subject,
            users: users,
        },
        {
            type: ColumnType.ENUM,
            name: 'Priority',
            getString: (issue) => toTitleCase(issue.priority),
            sortComparator: (a, b) => priorityComparator(a.priority!, b.priority!),
        },
        ...(props.type === ProductModule.CONTROLS
            ? [
                  {
                      type: ColumnType.MULTIPLE_CONTROLS as const,
                      name: 'Controls',
                      getControls: (issue: IssueResponse | IssueHistoryModificationsResponse) => (issue as IssueResponse).impacted_controls,
                      onClick: props.displayMappedControlsModal,
                  },
              ]
            : []),
        ...(props.type !== ProductModule.CONTROLS && !props.hideOrganizationInfo
            ? [
                  {
                      type: ColumnType.STRING as const,
                      name: props.type === ProductModule.UCRM ? 'Business' : 'Third Party',
                      getString: (issue: IssueResponse | IssueHistoryModificationsResponse) => getOrganizationName!(issue.impacted_vendor!),
                  },
              ]
            : []),
        {
            type: ColumnType.ENUM,
            name: 'Status',
            getString: (issue) => toTitleCase(issue.status),
            sortComparator: (a, b) => statusComparator(a.status, b.status),
        },
        ...(props.type !== ProductModule.CONTROLS && props.historical
            ? [
                  {
                      type: ColumnType.MULTIPLE_HISTORICAL_TAGS as const,
                      name: 'Tags',
                      getTags: (issue: IssueResponse | IssueHistoryModificationsResponse) => (issue as IssueHistoryModificationsResponse).tags,
                  },
              ]
            : [
                  {
                      type: ColumnType.MULTIPLE_TAGS as const,
                      name: 'Tags',
                      getTagIds: (issue: IssueResponse | IssueHistoryModificationsResponse) => (issue as IssueResponse).tags,
                      convertTagIdsToDisplayableTags: cachedData.convertTagIdsToDisplayableTags,
                  },
              ]),
    ];

    const filters: FilterConfig<IssueResponse | IssueHistoryModificationsResponse>[] = [
        ...(props.type !== ProductModule.CONTROLS && !props.hideOrganizationInfo
            ? [
                  {
                      type: FilterType.SINGLE_PROPERTY as const,
                      id: 'organizationFilter',
                      label: props.type === ProductModule.UCRM ? 'Filter by Business' : 'Filter by Third Party',
                      options: props.organizations.map((organization) => ({ name: organization.name, value: organization.id })),
                      getFilterableDataForRow: (issue: IssueResponse | IssueHistoryModificationsResponse) => (issue.impacted_vendor ? [issue.impacted_vendor] : []),
                      canSelectMultiple: true,
                      defaultSelections: props.preselectedOrganizationIdForFilter ? [props.preselectedOrganizationIdForFilter] : undefined,
                      disabled: props.disableOrganizationFilter,
                  },
              ]
            : []),
        {
            type: FilterType.MULTIPLE_PROPERTIES,
            id: 'priorityOrStatusFilter',
            label: 'Filter by Priority or Status',
            groups: [
                {
                    label: 'Priority',
                    options: [
                        { name: 'Low', value: IssuePriority.LOW },
                        { name: 'Medium', value: IssuePriority.MEDIUM },
                        { name: 'High', value: IssuePriority.HIGH },
                    ],
                    getFilterableDataForRow: (issue) => (issue.priority ? [issue.priority] : []),
                },
                {
                    label: 'Status',
                    options: [
                        { name: 'Open', value: IssueStatus.OPEN },
                        { name: 'Draft Open', value: IssueStatus.DRAFT_OPEN },
                        { name: 'Closed', value: IssueStatus.CLOSED },
                    ],
                    getFilterableDataForRow: (issue) => [issue.status],
                    defaultSelections: [IssueStatus.DRAFT_OPEN, IssueStatus.OPEN],
                },
            ],
        },
        {
            type: FilterType.USERS,
            id: 'ownerFilter',
            label: 'Filter by Owner',
            getUserIdsForRow: (issue) => (issue.owner_subject ? [issue.owner_subject] : []),
        },
        {
            type: FilterType.TAGS,
            id: 'tagsFilter',
            label: 'Filter by Tags',
            getTagIdsForRow: (issue) => (props.type !== ProductModule.CONTROLS && props.historical ? (issue as IssueHistoryModificationsResponse).tags.map((tag) => tag.id) : ((issue as IssueResponse).tags ?? [])),
        },
    ];

    const tableProps: TableProps<IssueResponse | IssueHistoryModificationsResponse> = {
        columns,
        filters,
        defaultSortColumn: 'Status',
        rowsData: props.issues,
        getUniqueIdForRow: (issue) => issue.id,
        pageVariant: (props.colorTheme ?? 'light') === 'light' ? 'standard' : 'dashboard',
    };

    const createButtonDestination: string = (() => {
        if (props.type === ProductModule.CONTROLS) {
            return getCreateIssueUrl(ProductModule.CONTROLS, props.createIssueOrigin, props.preselectedControlIdForCreate);
        } else {
            return getCreateIssueUrl(props.type, props.createIssueOrigin, props.preselectedOrganizationIdForCreate);
        }
    })();

    return (
        <>
            {!props.hideTitle ? (
                <>
                    <div className={styles.titleAndButtonContainer}>
                        <Text variant="Header2" color={props.colorTheme === 'dark' ? 'white' : 'blue'}>
                            Issues
                        </Text>
                        {!props.hideCreateButton && (
                            <Link variant={props.createButtonVariant} to={createButtonDestination} fontAwesomeImage={ICON_ADD_CREATE}>
                                Create Issue
                            </Link>
                        )}
                    </div>
                    {props.useHorizontalRuleBelowTitle && (
                        <div className={styles.horizontalRule}>
                            <hr />
                        </div>
                    )}
                </>
            ) : (
                <>
                    {!props.hideCreateButton && (
                        <div className={styles.alignRight}>
                            <Link variant={props.createButtonVariant} to={createButtonDestination} fontAwesomeImage={ICON_ADD_CREATE}>
                                Create Issue
                            </Link>
                        </div>
                    )}
                </>
            )}
            <Table {...tableProps} />
            {selectedHistoricalIssue && props.type !== ProductModule.CONTROLS && <IssueHistoryModal history={{ timestamp: selectedHistoricalIssue.last_updated_timestamp, issue: selectedHistoricalIssue }} documentApi={props.documentApi} hideModal={() => setSelectedHistoricalIssue(undefined)} />}
        </>
    );
};
